/**
 * File Name          : rwupdate-server.service.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LATEST_FW_IMAGE, LATEST_FW_VERSION } from '../../environments/fw-images';
import { environment } from '../../environments/environment';


export interface RWUpdateFileEntry {
  id: string;
  subsys: string;
  filename: string;
  versionMajor: string;
  versionMinor: string;
  versionBug: string;
  versionSpecial: string;
  name: string;
  description: string;
  baseLang: string;
  md5: string;
  fileDate: string;
}

export interface RWUpdateFileListResponse {
  sid: string;
  files: RWUpdateFileEntry[];
  dbStatus: string[];
}

/**
 * Shape of `latest-{channel}.json` served by the artifact file service.
 * See orgwide/fileservice/docs/DATASNAP-FIRMWARE-DISTRIBUTION.md#manifest-format
 */
export interface FirmwareChannelManifest {
  channel: string;
  version: {
    major: number;
    minor: number;
    bug: number;
    special: number;
  };
  versionString: string;
  bootloaderVersion: string;
  hardware: string;
  buildType: string;
  path: string;         // e.g. "v0.5.2/"
  image: string;        // e.g. "datasnap_mcuboot_image_v0_5_2_m2_dev_release.signed.encrypted.bin"
  imageSha256?: string;
  publishedAt: string;
  commitSha?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RWUpdateServerService {

  /**
   * In-memory cache of the channel manifest for the current app session.
   * A single fetch per session is sufficient — new firmware releases
   * take effect on next app open, which matches user expectations.
   */
  private manifestPromise: Promise<FirmwareChannelManifest | null> | null = null;

  constructor(private http: HttpClient) { }

  async isFWOutOfDate(version: {  versionMajor: number;
  versionMinor: number;
  versionBug: number;
  versionSpecial: number;}): Promise<boolean> {
    const latestVersion = await this.getLatestVersion();
    if (!latestVersion) {
      return false;
    }

    const currentParts = [
      version.versionMajor,
      version.versionMinor,
      version.versionBug,
      version.versionSpecial
    ];
    const latestParts = [
      latestVersion.versionMajor,
      latestVersion.versionMinor,
      latestVersion.versionBug,
      latestVersion.versionSpecial
    ];

    for (let i = 0; i < currentParts.length; i++) {
      const comparison = this.compareVersionPart(currentParts[i], latestParts[i]);
      if (comparison < 0) {
        return true;
      }
      if (comparison > 0) {
        return false;
      }
    }

    return false;
  }

  async getLatestFirmwareImage(): Promise<Blob> {
    // Try the file service when the feature is enabled.
    if (environment.firmwareService?.enabled) {
      try {
        const manifest = await this.fetchManifest();
        if (manifest) {
          const url = this.buildImageUrl(manifest);
          return await firstValueFrom(
            this.http.get(url, { responseType: 'blob' })
          );
        }
      } catch (err) {
        console.warn(
          '[RWUpdateServerService] File-service firmware fetch failed; falling back to bundled asset.',
          err
        );
      }
    }

    // Fallback: bundled asset shipped with the Angular bundle.
    // This path stays valid until Phase 4 of the file-service cutover.
    return await firstValueFrom(
      this.http.get(`assets/ota/${LATEST_FW_IMAGE}`, {
        responseType: 'blob'
      })
    );
  }

  /**
   * Resolve the latest firmware version, preferring the file-service manifest
   * when the feature flag is enabled. Falls back to the bundled
   * `LATEST_FW_VERSION` constant if the fetch fails or the feature is off.
   */
  private async getLatestVersion(): Promise<{
    versionMajor: number;
    versionMinor: number;
    versionBug: number;
    versionSpecial: number;
  } | null> {
    if (environment.firmwareService?.enabled) {
      const manifest = await this.fetchManifest();
      if (manifest) {
        return {
          versionMajor: manifest.version.major,
          versionMinor: manifest.version.minor,
          versionBug: manifest.version.bug,
          versionSpecial: manifest.version.special
        };
      }
    }
    return LATEST_FW_VERSION ?? null;
  }

  /**
   * Fetch and cache the channel manifest. Returns null on failure — callers
   * are expected to fall back to bundled behavior.
   */
  private fetchManifest(): Promise<FirmwareChannelManifest | null> {
    if (this.manifestPromise) {
      return this.manifestPromise;
    }
    const cfg = environment.firmwareService;
    if (!cfg?.enabled || !cfg.baseUrl || !cfg.channel) {
      return Promise.resolve(null);
    }

    const url = `${cfg.baseUrl}/api/download`
      + `?tier=public`
      + `&path=${encodeURIComponent(`crexo/datasnap/firmware/latest-${cfg.channel}.json`)}`;

    this.manifestPromise = firstValueFrom(
      this.http.get<FirmwareChannelManifest>(url)
    ).then(manifest => {
      // Minimal shape validation — protects against a corrupted manifest
      // silently disabling update prompts.
      if (
        !manifest ||
        typeof manifest.version?.major !== 'number' ||
        typeof manifest.image !== 'string' ||
        typeof manifest.path !== 'string'
      ) {
        throw new Error('Manifest missing required fields');
      }
      return manifest;
    }).catch(err => {
      console.warn(
        '[RWUpdateServerService] Failed to fetch channel manifest; will use bundled fallback for this session.',
        err
      );
      // Don't cache the failure — a transient network blip during app open
      // shouldn't disable file-service fetch for the rest of the session.
      this.manifestPromise = null;
      return null;
    });

    return this.manifestPromise;
  }

  /**
   * Build the file-service URL for the encrypted, signed image referenced by
   * the manifest. Uses the same `/api/download` public-tier endpoint as the
   * manifest fetch so both requests go through the SWA custom domain.
   */
  private buildImageUrl(manifest: FirmwareChannelManifest): string {
    const cfg = environment.firmwareService!;
    const path = `crexo/datasnap/firmware/${manifest.path}${manifest.image}`;
    return `${cfg.baseUrl}/api/download?tier=public&path=${encodeURIComponent(path)}`;
  }

  private compareVersionPart(current: string | number, latest: string | number): number {
    const currentNum = Number(current);
    const latestNum = Number(latest);
    const currentText = String(current).trim();
    const latestText = String(latest).trim();
    const currentIsNumeric = currentText !== '' && Number.isFinite(currentNum);
    const latestIsNumeric = latestText !== '' && Number.isFinite(latestNum);

    if (currentIsNumeric && latestIsNumeric) {
      return currentNum - latestNum;
    }

    return currentText.localeCompare(latestText, undefined, { numeric: true, sensitivity: 'base' });
  }
}
